/**
 * Authentication Incentive Modal — paywalls du funnel
 *
 * Doctrine pricing 11/05/2026 :
 * - Anonymous (limit=3) : a consommé ses 3 IA gratuites → CTA "Créer un compte
 *   pour 5 de plus", rappel que le manuel reste illimité sans compte.
 * - Free (limit=5)      : a consommé ses 5 IA Free → CTA "Passer Solo" (30€/an
 *   pour IA illimitée + features).
 *
 * Le composant distingue les deux cas via `currentUsage.maxConversations`
 * (3 = anonyme, 5 = free, autre = générique).
 */

import React from "react";
import {
  X,
  Crown,
  MessageCircle,
  Calendar,
  Sparkles,
  ExternalLink,
  PencilLine,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { type AuthIncentiveType } from "../../services/QuotaService";
import { Link } from "react-router-dom";

type TFunc = (key: string) => string;

interface AuthIncentiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignUp: () => void;
  onSignIn: () => void;
  trigger: AuthIncentiveType;
  currentUsage?: {
    conversations: number;
    maxConversations: number;
    polls?: number;
    maxPolls?: number;
  };
}

interface ModalContent {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  description: string;
  /** CTA principal (action positive forte). */
  primaryCta: {
    label: string;
    onClick: (handlers: { onSignUp: () => void }) => void;
    icon: React.ReactNode;
  };
  /** CTA secondaire (action moindre, garde le user dans le produit). */
  secondaryCta?: {
    label: string;
    onClick: (handlers: { onClose: () => void }) => void;
  };
  /** Lien tertiaire (sign-in pour les comptes existants). */
  signInLabel?: string;
}

function getModalContent(
  t: TFunc,
  trigger: AuthIncentiveType,
  currentUsage?: AuthIncentiveModalProps["currentUsage"],
): ModalContent {
  const maxConversations = currentUsage?.maxConversations ?? 0;

  // === Moment 1 : anonyme a consommé ses 3 IA (limite anonyme = 3) ===
  if (
    (trigger === "conversation_limit" || trigger === "feature_unlock") &&
    maxConversations === 3
  ) {
    return {
      title: t("auth_incentive.anon_title"),
      subtitle: t("auth_incentive.anon_subtitle"),
      icon: <Sparkles className="w-12 h-12 text-purple-400" />,
      description: t("auth_incentive.anon_description"),
      primaryCta: {
        label: t("auth_incentive.anon_primary_cta"),
        onClick: ({ onSignUp }) => onSignUp(),
        icon: <Crown className="w-4 h-4" />,
      },
      secondaryCta: {
        label: t("auth_incentive.anon_secondary_cta"),
        onClick: ({ onClose }) => onClose(),
      },
      signInLabel: t("auth_incentive.sign_in_label"),
    };
  }

  // === Moment 2 : Free a consommé ses 5 IA (limite free = 5) ===
  if (
    (trigger === "conversation_limit" || trigger === "feature_unlock") &&
    maxConversations === 5
  ) {
    return {
      title: t("auth_incentive.free_title"),
      subtitle: t("auth_incentive.free_subtitle"),
      icon: <Crown className="w-12 h-12 text-purple-400" />,
      description: t("auth_incentive.free_description"),
      primaryCta: {
        label: t("auth_incentive.solo_cta"),
        onClick: ({ onSignUp: _ }) => {
          window.location.href = "/pricing";
        },
        icon: <Crown className="w-4 h-4" />,
      },
      secondaryCta: {
        label: t("auth_incentive.free_secondary_cta"),
        onClick: ({ onClose }) => {
          onClose();
          window.location.href = "/pricing";
        },
      },
    };
  }

  // === Moment 3 : limite polls actifs (10 pour Free) ===
  if (trigger === "poll_limit") {
    return {
      title: t("auth_incentive.poll_limit_title"),
      subtitle: t("auth_incentive.poll_limit_subtitle"),
      icon: <Calendar className="w-12 h-12 text-orange-400" />,
      description: t("auth_incentive.poll_limit_description"),
      primaryCta: {
        label: t("auth_incentive.solo_cta"),
        onClick: ({ onSignUp: _ }) => {
          window.location.href = "/pricing";
        },
        icon: <Crown className="w-4 h-4" />,
      },
      secondaryCta: {
        label: t("auth_incentive.poll_limit_secondary_cta"),
        onClick: ({ onClose }) => {
          onClose();
          window.location.href = "/dashboard";
        },
      },
    };
  }

  // === Fallback générique ===
  return {
    title: t("auth_incentive.generic_title"),
    subtitle: t("auth_incentive.generic_subtitle"),
    icon: <MessageCircle className="w-12 h-12 text-purple-400" />,
    description: t("auth_incentive.generic_description"),
    primaryCta: {
      label: t("auth_incentive.generic_primary_cta"),
      onClick: ({ onSignUp }) => onSignUp(),
      icon: <Crown className="w-4 h-4" />,
    },
    signInLabel: t("auth_incentive.sign_in_label"),
  };
}

const AuthIncentiveModal: React.FC<AuthIncentiveModalProps> = ({
  isOpen,
  onClose,
  onSignUp,
  onSignIn,
  trigger,
  currentUsage,
}) => {
  const { t } = useTranslation("polls");

  if (!isOpen) return null;

  const content = getModalContent(t, trigger, currentUsage);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1e1e1e] rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="relative p-6 text-center border-b border-gray-700">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-[#2a2a2a] rounded-full transition-colors"
            data-testid="authincentivemodal-button"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>

          <div className="flex justify-center mb-4">{content.icon}</div>

          <h2 className="text-xl font-bold text-gray-100 mb-2">
            {content.title}
          </h2>

          <p className="text-gray-300 text-sm">{content.subtitle}</p>
        </div>

        <div className="p-6">
          <p className="text-gray-200 mb-6 text-center">
            {content.description}
          </p>

          {/* Lien d'info pricing/quotas */}
          <div className="mb-6">
            <div className="p-3 bg-[#0a0a0a] rounded-lg border border-gray-700">
              <Link
                to="/pricing"
                className="flex items-center justify-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                onClick={onClose}
              >
                <ExternalLink className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {t("auth_incentive.view_plans_link")}
                </span>
              </Link>
            </div>
          </div>

          <div className="space-y-3" data-testid="auth-required">
            <button
              onClick={() => content.primaryCta.onClick({ onSignUp })}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="authincentivemodal-primary-cta"
            >
              {content.primaryCta.icon}
              {content.primaryCta.label}
            </button>

            {content.secondaryCta && (
              <button
                onClick={() => content.secondaryCta!.onClick({ onClose })}
                className="w-full bg-[#0a0a0a] hover:bg-[#2a2a2a] text-gray-300 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="authincentivemodal-secondary-cta"
              >
                <PencilLine className="w-4 h-4" />
                {content.secondaryCta.label}
              </button>
            )}

            {content.signInLabel && (
              <button
                onClick={onSignIn}
                className="w-full text-gray-400 hover:text-gray-200 font-medium py-2 px-4 transition-colors text-sm"
                data-testid="sign-in"
              >
                {content.signInLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthIncentiveModal;
